import React, { useContext, useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { ThemeContext } from 'styled-components'
import { isEmpty } from 'lodash'
import { Redirect } from 'react-router-dom'

// config
import { Countries } from '@navent-jobs/config'

// hooks
import { useWindowSize } from '@navent-jobs/ui-kit/hooks/window-size'

// ui-kit
import { MainContainer, Row, Col } from '@navent-jobs/ui-kit/components/grid'
import { Icon } from '@navent-jobs/ui-kit/components/icon'
import { RedirectHeaderMeta } from '@navent-jobs/ui-kit/components/redirect-prerender-meta'

// components
import Breadcrumbs from './components/breadcrumbs'
import ListadoAvisos from './components/listado-avisos'
import { BusquedasRecientes } from './components/busquedas-recientes'
import FiltersSection from './components/filters-section'
import Metas from './components/metas'
import CantidadEmpleos from './components/cantidad-empleos'
import SeoFooter from './components/seo-footer'
import BannerWithFilter from './components/banner-with-filter'
import CreateAlertButton from './components/crear-alerta-button'
import CreateAlertSection from './components/crear-alerta-section'
import { getUrlParamByName } from '../../hooks/get-url-param'
import Sort from '../../components/sort'

// mixins
import {
  BreadcrumbContainer,
  MainPage,
  PageContent,
  SidebarContainer,
  ColFiltro,
  ColListado,
  SortCantidadEmpleosContainer,
  SpacerMobile,
  FlexContainer,
  CustomTitle,
} from './mixins'

// constants
import { DataLayers as PageDataLayers } from '../../constants/gtm-events-listado-page'
import features from '../../constants/features-per-country'
import { ID_PAIS, SITE_ID } from '../../constants'
import { iaOrigenRecomendados } from '../../constants/origen-avisos-recomendados'

// services
import { makeTitleListado } from '../../services/meta-tags-listado'
import ModalAlerta from '../../components/modals/modal-alerta/ModalAlerta'

// redux
import { showSnackbar, showSnackbarError } from '../../redux/actions/applicant/curriculum/snackbar'
import ModalAvisosRecomendados from '../../components/modals/modal-avisos-recomendados'
import TextoRecomendados from './components/texto-recomendados'
import EmptyRecomendados from './components/empty-recomendados'
import { getRecomendadosListado } from '../../redux/actions/recomendados'

// utils
import { isLegacySubareaURL, subareaRedirectURL } from '../../utils/search-avisos-v2'

const recomendadosEnabled = features[SITE_ID || 'BMAR'].listadoAvisos?.recomendados?.enabled
const searchAvisosV2Enabled = features[SITE_ID || 'BMAR'].searchAvisosV2?.enabled

const RECOMMENDED_JOB_POSTING_AMOUNT = 5
const SHOW_RECOMMENDED_JOB_POSTING_AMOUNT = 5
const IA_ORIGEN = iaOrigenRecomendados.listados

const loadApplicantData = (applicant, isUserLoggedIn, searchParamContent, dispatch) => {
  const isSearchParamContent = searchParamContent === 'true'
  const isMexico = ID_PAIS === Countries.MX.id
  const validatedEmail = isUserLoggedIn && applicant?.mailValidado

  if (isSearchParamContent && validatedEmail && !isMexico) {
    dispatch(showSnackbar('¡Activaste tu cuenta con éxito!', 'icon-light-checkmark-circle-1'))
  } else if (searchParamContent && !isSearchParamContent && !validatedEmail) {
    dispatch(showSnackbarError('Hubo un error, vuelve a intentarlo.'))
  }
}

const ListadoAvisosPage = ({
  listadoAvisos,
  location,
  match,
  facetBanner,
  isUserLoggedIn,
  applicant,
  avisosRecomendados,
  labsParams,
  loadingRecomendados,
  avisosListado,
}) => {
  const { isMobile, isTabletPortrait, isTabletLandscape, isDesktop } = useWindowSize()
  const dispatch = useDispatch()
  const { colors } = useContext<{ colors }>(ThemeContext)

  const resultsNotFound = listadoAvisos.error && listadoAvisos.totalAvisos === 0 && listadoAvisos.avisos === null
  const isMobileOrTabletPortrait = isMobile || isTabletPortrait
  const isDesktopOrTabletLandscape = isDesktop || isTabletLandscape
  const queryParams = new URLSearchParams(location.search)
  const hasFacetBanner = facetBanner.enabled === true
  const searchParamContent = getUrlParamByName('activation_success')
  const [showModalRecomendados, setShowModalRecomendados] = useState(false)
  const iaOrigen = `ia_origen=${IA_ORIGEN}`
  const labsParamsStr = !isEmpty(labsParams)
    ? `?${new URLSearchParams(labsParams).toString()}&${iaOrigen}`
    : `?${iaOrigen}`
  const showRecommendedLabel =
    recomendadosEnabled && isUserLoggedIn && avisosListado && avisosListado.length > SHOW_RECOMMENDED_JOB_POSTING_AMOUNT

  const getAvisosYMostrarModal = () => {
    dispatch(
      getRecomendadosListado({
        limit: RECOMMENDED_JOB_POSTING_AMOUNT,
        source: 'empty_list_recomendados_postulante',
      })
    )
    setShowModalRecomendados(true)
  }

  useEffect(() => {
    if (applicant) {
      loadApplicantData(applicant, isUserLoggedIn, searchParamContent, dispatch)
    }
  }, [applicant])

  useEffect(() => {
    document.title = makeTitleListado({})
  }, [])

  if (searchAvisosV2Enabled && isLegacySubareaURL(window.location.pathname)) {
    const redirectURL = subareaRedirectURL(window.location.pathname, match)
    if (redirectURL) {
      const fullRedirectURL = `${redirectURL}${location.search}${location.hash}`
      return (
        <>
          <RedirectHeaderMeta url={fullRedirectURL} contentCode="301" />
          <Redirect to={fullRedirectURL} />
        </>
      )
    }
  }

  return (
    <PageContent>
      <Metas />

      <BannerWithFilter />

      <MainPage>
        <MainContainer>
          {isDesktopOrTabletLandscape && (
            <Row>
              <Col>
                <BreadcrumbContainer>
                  <Breadcrumbs />
                </BreadcrumbContainer>
              </Col>
            </Row>
          )}
          <Row>
            {isMobileOrTabletPortrait && (
              <>
                <ColFiltro>
                  <FiltersSection />

                  <SortCantidadEmpleosContainer facetbanner={facetBanner} isMobile={isMobile}>
                    {!hasFacetBanner && <CantidadEmpleos />}

                    {!isMobile && (
                      <Sort path={location.pathname} queryParams={queryParams} dataLayers={PageDataLayers} />
                    )}
                  </SortCantidadEmpleosContainer>

                  <CreateAlertSection />

                  {isMobileOrTabletPortrait && showRecommendedLabel && (
                    <>
                      <SpacerMobile />
                      <TextoRecomendados setShowModalRecomendados={getAvisosYMostrarModal} />
                    </>
                  )}

                  {isMobile && <Sort path={location.pathname} queryParams={queryParams} dataLayers={PageDataLayers} />}
                </ColFiltro>
              </>
            )}

            {isDesktopOrTabletLandscape &&
              (!resultsNotFound ? (
                <Col xs={{ size: 12 }} lg={{ size: 4 }} xl={{ size: 3 }}>
                  <CreateAlertButton />
                  <SidebarContainer>
                    <FiltersSection />
                  </SidebarContainer>
                </Col>
              ) : (
                <Col xs={{ size: 12, order: 2 }} lg={{ size: 4, order: 1 }} xl={{ size: 3, order: 1 }}>
                  <BusquedasRecientes loading={listadoAvisos.loading} />
                </Col>
              ))}

            <ColListado id="listado-avisos">
              {!isMobileOrTabletPortrait && (
                <SortCantidadEmpleosContainer>
                  <CantidadEmpleos />
                  <Sort path={location.pathname} queryParams={queryParams} dataLayers={PageDataLayers} />
                </SortCantidadEmpleosContainer>
              )}

              {!isMobileOrTabletPortrait && showRecommendedLabel && (
                <TextoRecomendados setShowModalRecomendados={getAvisosYMostrarModal} />
              )}

              <ListadoAvisos parentRoute={{ location, match }} resultsNotFound={resultsNotFound} />
            </ColListado>
          </Row>
        </MainContainer>
      </MainPage>

      {!isDesktopOrTabletLandscape && (
        <BreadcrumbContainer>
          <MainContainer>
            <Breadcrumbs />
          </MainContainer>
        </BreadcrumbContainer>
      )}

      <ModalAlerta />
      <SeoFooter />
      <ModalAvisosRecomendados
        primaryButtonAction={() => setShowModalRecomendados(false)}
        primaryButtonText="Ir al listado"
        title={
          <FlexContainer>
            <Icon name="icon-light-ia-stars" size={isMobile ? '16' : '24'} color={colors.secondary.normal} />
            <CustomTitle>Empleos relacionados según tus búsquedas</CustomTitle>
          </FlexContainer>
        }
        avisos={avisosRecomendados}
        width={920}
        visible={showModalRecomendados}
        closeModal={() => setShowModalRecomendados(false)}
        subtitle=""
        emptyComponent={<EmptyRecomendados />}
        labsParams={labsParamsStr}
        loading={loadingRecomendados}
      />
    </PageContent>
  )
}

const mapStateToProps = ({ listadoAvisosStore, applicantStore, recomendadosStore }) => ({
  listadoAvisos: listadoAvisosStore,
  facetBanner: listadoAvisosStore.facetBanner,
  isUserLoggedIn: applicantStore && applicantStore.applicant,
  applicant: applicantStore ? applicantStore.applicant : {},
  loadingRecomendados: recomendadosStore.status.loading,
  avisosRecomendados: recomendadosStore.avisos,
  labsParams: recomendadosStore.labsParams,
  avisosListado: listadoAvisosStore.avisos,
})

export default connect(mapStateToProps)(ListadoAvisosPage)
